import Table from '@/components/table/Table';
import { convertedQtyFormatterConfig, defaultColumn } from '@/config/config';
import { parseLuceraBlotterOrder } from '@/helpers/blotterHelper';
import { orderColumns } from '@/helpers/table/luceraOrderBlotterTableHelper';
import { LuceraBlotterItem } from '@/model/blotters';
import { useAppSelector } from '@/state/hooks';
import { selectCredentials, selectCurrentAccount } from '@/state/reducers/authSlice';
import { selectGeneralSettings } from '@/state/reducers/settingSlice';
import useLanguage from '@/utils/hooks/useLanguage';
import {
    SortingState,
    getCoreRowModel,
    getFacetedMinMaxValues,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useLocale, useNumberFormatter } from 'react-aria';
import { mapOrderBlotters } from './LuceraOrderBlotterTable';

interface LuceraOrderHistoryTableProps {
    orderHistories: LuceraBlotterItem[];
    setCSVData: (data: any[]) => void;
}

const LuceraOrderHistoryTable = (props: LuceraOrderHistoryTableProps) => {
    const { orderHistories, setCSVData } = props;

    const lang = useLanguage();
    const { locale } = useLocale();

    const credentials = useAppSelector(selectCredentials);
    const currentAccount = useAppSelector(selectCurrentAccount);
    const generalSettings = useAppSelector(selectGeneralSettings);

    const formatNumber = useNumberFormatter(convertedQtyFormatterConfig);

    const [sorting, setSorting] = useState<SortingState>([{ id: 'time', desc: false }]);

    const formattedTrades: any = useMemo(() => {
        const orderBlotters = orderHistories.map((order) =>
            mapOrderBlotters(parseLuceraBlotterOrder(order), locale, formatNumber)
        );
        return orderBlotters;
    }, [orderHistories, locale, currentAccount]);

    const columns = useMemo(() => orderColumns(lang, generalSettings), [lang, generalSettings]);
    // const columns = useMemo(() => historyColumns(lang, generalSettings), [lang, generalSettings]);

    const tableInstance = useReactTable({
        data: formattedTrades,
        columns,
        state: { sorting },
        defaultColumn,
        enableColumnResizing: true,
        columnResizeMode: 'onChange',
        globalFilterFn: 'includesString',
        onSortingChange: (sorting) => setSorting(sorting),
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        getSubRows: (row: any) => row.subRows,
        enableRowSelection: true
    });

    useEffect(() => {
        setCSVData(formattedTrades);
    }, [formattedTrades]);

    return <Table tableInstance={tableInstance} isLoadingTableContent={false} />;
};

export default LuceraOrderHistoryTable;
